import {
  Button,
  InputGroup,
  InputSelect,
  NO_REGEX,
  SWIFT_CODE_REGEX,
  color,
} from "@funded-here-interface/common";
import { FC, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useStyle } from "../../styles/BankDetailsCss";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "@funded-here-interface/investor-onboarding/src/store";
import { ToastContainer, toast } from "react-toastify";
import {
  setBankNameRedux,
  setBankCodeRedux,
  setAccountNoRedux,
  setAccountTypeRedux,
  setAccountHolderNameRedux,
  setSwiftCodeReduct,
  setBankAddressRedux,
  setBankCountryRedux,
  setCurrencyRedux,
} from "../../features/institutionalInvestor/bankSlice";
import { setCompanyRedux } from "../../features/institutionalInvestor/bizSlice";
import "react-toastify/dist/ReactToastify.css";
import { bankAccountTypes } from "../../constants/bank";
import OnboardingLayout from "../../layouts/OnboardingLayout/OnboardingLayout";
import BankAccNameDisclaimer from "../../components/BankAccNameDisclaimer/BankAccNameDisclaimer";
import { Persona } from "@funded-here-interface/common/src/constant/enum";
import useGetOrgDraft from "../../hooks/useGetOrgDraft";
import useSaveOrgDraft from "../../hooks/useSaveOrgDraft";
import {
  countries,
  AccountCurrencyTypeEnum,
} from "@funded-here-interface/common/src/constant/country";
import {
  getHongKongBankOptions,
  isHongKong,
  getBankCodeByName,
  getBankNameByCode,
} from "@funded-here-interface/common/src/constant/hongKongBanks";

const currencyOptions = Object.values(AccountCurrencyTypeEnum);
const hongKongBankOptions = getHongKongBankOptions();

const BankDetails: FC = () => {
  const { classes } = useStyle();
  const Navigate = useNavigate();
  const dispatch = useDispatch();
  const type = useSelector((state: RootState) => state.auth.type);

  const userId = useSelector((state: RootState) => state.auth.userId);

  const getOrgDraftMutation = useGetOrgDraft();
  const saveOrgDraftMutation = useSaveOrgDraft();

  const [initPage, setInitPage] = useState(true);

  const { company } = useSelector((state: RootState) => state.biz);
  const { token } = useSelector((state: RootState) => state.auth);

  const [bankName, setBankName] = useState("");
  const [bankCode, setBankCode] = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const [accountType, setAccountType] = useState("");
  const [swiftCode, setSwiftCode] = useState("");
  const [accountHolderName, setAccountHolderName] = useState("");
  const [bankAddress, setBankAddress] = useState("");
  const [bankCountry, setBankCountry] = useState("");
  const [currency, setCurrency] = useState("");
  const [accountHolderNameError, setAccountHolderNameError] = useState("");
  const navTimerRef = useRef<ReturnType<typeof setTimeout>>();

  // Cleanup navigation timer on unmount
  useEffect(() => () => { clearTimeout(navTimerRef.current); }, []);

  const handleBack = () => {
    saveDraft(false);

    if (type === Persona.INVESTOR) {
      Navigate("/institutional-rep");
    } else {
      Navigate("/representative-details");
    }
  };

  const [error, setError] = useState({
    bank_name: "",
    bankAccountNo: "",
    account_type: "",
    swift_code: "",
    account_holder_name: "",
    accountType: "",
    bank_address: "",
    bank_country: "",
    currency: "",
  });

  const handleNumericInputChange = (actualOnChange: any) => (value: any) => {
    const numericValue = value.replace(NO_REGEX, "");
    actualOnChange(numericValue);
  };

  /**
   * Validate account holder name matches company name
   */
  const validateAccountHolderName = (): boolean => {
    const hasError =
      accountHolderName && company && accountHolderName !== company;
    return !hasError;
  };

  const handleNext = () => {
    const customErr = { ...error };
    let valid = true;

    if (!bankName) {
      valid = false;
      customErr.bank_name = "Bank Name is required";
    }
    if (!swiftCode) {
      valid = false;
      customErr.swift_code = "Swift Code is required";
    }
    if (!accountNumber) {
      valid = false;
      customErr.bankAccountNo = "Bank Account no is required";
    }

    if (!accountType) {
      valid = false;
      customErr.accountType = "Account Type is required";
    }
    if (!accountHolderName) {
      valid = false;
      customErr.account_holder_name = "Account Holder Name is required";
    }

    if (!bankAddress) {
      valid = false;
      customErr.bank_address = "Bank Address is required";
    }

    if (!bankCountry) {
      valid = false;
      customErr.bank_country = "Bank Country is required";
    }

    if (!currency) {
      valid = false;
      customErr.currency = "Currency is required";
    }

    // Check account holder name matches company name
    if (company && accountHolderName && accountHolderName !== company) {
      valid = false;
      customErr.account_holder_name =
        "Account holder name must match the company name";
      toast.error("Account holder name must match the company name", {
        autoClose: 2000,
      });
    }

    if (valid) {
      // Save draft only when all validations pass
      saveDraft(false);

      // Dispatch bank details to Redux
      dispatch(setBankNameRedux(bankName));
      dispatch(setBankCodeRedux(bankCode));
      dispatch(setAccountNoRedux(accountNumber));
      dispatch(setAccountTypeRedux(accountType));
      dispatch(setSwiftCodeReduct(swiftCode));
      dispatch(setAccountHolderNameRedux(accountHolderName));
      dispatch(setBankAddressRedux(bankAddress));
      dispatch(setBankCountryRedux(bankCountry));
      dispatch(setCurrencyRedux(currency));

      navTimerRef.current = setTimeout(() => {
        Navigate("/doc-upload");
      }, 500);
    } else {
      setError(customErr);
    }
  };

  const fetchDraft = async (userId: number) => {
    try {
      const params = {
        userId: userId,
        token,
      };

      const result = await getOrgDraftMutation.mutateAsync(params);
      return result;
    } catch (error) {
      console.error("Error fetching draft:", error);
    }
  };

  useEffect(() => {
    const initializeDraft = async () => {
      if (initPage) {
        try {
          const data = await fetchDraft(parseInt(userId));

          if (data) {
            setBankName(data.bankName || "");
            setBankCode(data.bankCode || "");
            setAccountType(data.accountType || "");
            setAccountNumber(data.accountNumber || "");
            setAccountHolderName(data.accountHolderName || "");
            setSwiftCode(data.swiftCode || "");
            setBankAddress(data.bankAddress || "");
            setBankCountry(data.bankCountry || "");
            setCurrency(data.currency || "");

            // If Hong Kong and we have bankCode but no bankName, resolve bankName from code
            if (isHongKong(data.bankCountry) && data.bankCode && !data.bankName) {
              const resolvedBankName = getBankNameByCode(data.bankCode);
              if (resolvedBankName) {
                setBankName(resolvedBankName);
              } else {
                console.warn(`Invalid Hong Kong bank code in draft: ${data.bankCode}`);
              }
            }

            // Hydrate company name to Redux for validation
            if (data.companyName) {
              dispatch(setCompanyRedux(data.companyName));
            }
          }
        } catch (error) {
          toast.error(`Error fetching draft: ${error}`);
        } finally {
          setInitPage(false);
        }
      }
    };

    initializeDraft();
  }, [initPage, userId]);

  const saveDraft = (showToast: Boolean = true) => {
    const isAccountHolderNameValid = validateAccountHolderName();

    // Show error message below input and toast if account holder name is invalid
    if (!isAccountHolderNameValid) {
      setAccountHolderNameError(
        "Account holder name must match the company name"
      );
      toast.error("Account holder name must match the company name", {
        autoClose: 2000,
      });
    } else {
      setAccountHolderNameError("");
    }

    // Check if there are other fields to save (besides accountHolderName)
    const hasOtherFieldsToSave =
      bankName ||
      accountNumber ||
      accountType ||
      swiftCode ||
      bankAddress ||
      bankCountry ||
      currency;

    // If only accountHolderName is filled and it's invalid, don't save
    if (!isAccountHolderNameValid && !hasOtherFieldsToSave) {
      return;
    }

    // If bank country is not Hong Kong, explicitly clear bankCode
    const clearFields: ("bankCode")[] = !isHongKong(bankCountry)
      ? ["bankCode"]
      : [];

    // Prepare save payload
    const draftData: any = {
      userId: parseInt(userId),
      token: token,
      bankName: bankName,
      bankCode: bankCode || null,
      accountNumber: accountNumber,
      accountType: accountType,
      swiftCode: swiftCode,
      bankAddress: bankAddress,
      bankCountry: bankCountry,
      currency: currency,
      clearFields,
    };

    // Only include accountHolderName if valid
    if (isAccountHolderNameValid) {
      draftData.accountHolderName = accountHolderName;
    }

    saveOrgDraftMutation.mutateAsync(draftData, {
      onSuccess: () => {
        if (showToast && hasOtherFieldsToSave) {
          toast.success("Draft Saved", { autoClose: 2000 });
        }
      },
      onError: (e) => {
        toast.error((e as Error).message);
      },
    });
  };

  return (
    <OnboardingLayout
      step={3}
      showSteps={true}
      content={
        <div style={{ marginTop: "70px" }}>
          <BankAccNameDisclaimer />
          <InputGroup
            isErrored={error.account_holder_name || accountHolderNameError}
            errorMessage={
              !!(error.account_holder_name || accountHolderNameError)
            }
            onChange={(value: string) => {
              setError((prev) => ({ ...prev, account_holder_name: "" }));
              setAccountHolderName(value);
              if (accountHolderNameError) {
                setAccountHolderNameError("");
              }
            }}
            labelText="Account Holder Name"
            value={accountHolderName}
            id="account_holder_name"
          />
          <InputSelect
            isErrored={error.bank_country}
            errorMessage
            onChange={(value: string) => {
              setError((prev) => ({ ...prev, bank_country: "", bank_name: "" }));
              setBankCountry(value);
              // Clear bank name and code when country changes to ensure correct input type is used
              setBankName("");
              setBankCode("");
            }}
            labelText="Bank Country"
            id="bank_country"
            value={bankCountry}
            data={countries}
          />
          <InputGroup
            isErrored={error.bank_address}
            errorMessage
            onChange={(value: string) => {
              setError((prev) => ({ ...prev, bank_address: "" }));
              setBankAddress(value);
            }}
            labelText="Bank Address"
            value={bankAddress}
            id="bank_address"
          />
          {isHongKong(bankCountry) ? (
            <InputSelect
              isErrored={error.bank_name}
              errorMessage
              onChange={(value: string) => {
                setError((prev) => ({ ...prev, bank_name: "" }));
                setBankName(value);
                // Set bank code when Hong Kong bank is selected
                const code = getBankCodeByName(value);
                if (!code) {
                  console.warn(`Bank code not found for: ${value}`);
                }
                setBankCode(code || "");
              }}
              labelText="Bank Name"
              id="bank_name"
              value={bankName}
              data={hongKongBankOptions}
              disabled={!bankCountry}
            />
          ) : (
            <InputGroup
              isErrored={error.bank_name}
              errorMessage
              onChange={(value: string) => {
                setError((prev) => ({ ...prev, bank_name: "" }));
                setBankName(value);
              }}
              labelText="Bank Name"
              id="bank_name"
              value={bankName}
              disabled={!bankCountry}
            />
          )}
          <InputGroup
            isErrored={error.bankAccountNo}
            errorMessage
            onChange={handleNumericInputChange((value: any) => {
              setError((prev) => ({ ...prev, bankAccountNo: "" }));
              setAccountNumber(value.replace(NO_REGEX, ""));
            })}
            labelText="Bank Account Number"
            id="account_number"
            value={accountNumber}
          />
          <InputSelect
            isErrored={error?.accountType}
            errorMessage
            onChange={(value: string) => {
              setError((prev) => ({ ...prev, accountType: "" }));
              setAccountType(value);
            }}
            labelText="Bank Account Type"
            id="accountType"
            value={accountType}
            data={bankAccountTypes}
          />
          <InputGroup
            isErrored={error.swift_code}
            errorMessage
            onChange={(value: string) => {
              setError((prev) => ({ ...prev, swift_code: "" }));
              setSwiftCode(value.replace(SWIFT_CODE_REGEX, ""));
            }}
            labelText="Bank SWIFT Code"
            value={swiftCode}
            id="swift_code"
          />
          <InputSelect
            isErrored={error.currency}
            errorMessage
            onChange={(value: string) => {
              setError((prev) => ({ ...prev, currency: "" }));
              setCurrency(value);
            }}
            labelText="Currency"
            id="currency"
            value={currency}
            data={currencyOptions}
          />
          <div className={classes.buttonDiv}>
            <div className={classes.buttonWrapper}>
              <Button
                onClick={handleBack}
                backgroundColor={color.WHITE}
                textColor={color.FHGREEN}
                children="Back"
                border="1px solid #338F86"
                width="100%"
              />
            </div>
            <ToastContainer />
            <div className={classes.buttonWrapper}>
              <Button
                onClick={handleNext}
                textColor={color.WHITE}
                backgroundColor={color.FHGREEN}
                children="Next"
                width="100%"
              />
            </div>
          </div>
          <div className={classes.buttonWrapper}>
            <Button
              onClick={saveDraft}
              textColor={color.WHITE}
              backgroundColor={color.LOANTEXT}
              children="Save Draft"
              width="100%"
            />
          </div>
        </div>
      }
    />
  );
};

export default BankDetails;
